import type { GeneratedRoutePoint, UserRideDetail, UserRideSummary } from "../data/rideService";
import { routePointAltitudeMeters } from "./routeElevation";

export type ExportableWaypoint = {
  latitude: number;
  longitude: number;
  title?: string;
  description?: string;
  type?: string;
  symbol?: string;
  km?: string;
  openTime?: string;
  closeTime?: string;
  visibleToAthletes?: boolean;
};

export type ExportableRoute = Pick<UserRideDetail | UserRideSummary, "title" | "routePoints"> & {
  controlPoints?: GeneratedRoutePoint[];
  metadataTime?: number;
  waypoints?: ExportableWaypoint[];
};

function xmlEscape(value: unknown) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function pointTime(point: GeneratedRoutePoint) {
  if (!point.timestamp || !Number.isFinite(point.timestamp) || point.timestamp <= 0) return "";
  return `\n        <time>${new Date(point.timestamp).toISOString()}</time>`;
}

function pointElevation(point: GeneratedRoutePoint) {
  const altitude = routePointAltitudeMeters(point);
  if (altitude == null) return "";
  return `\n        <ele>${altitude.toFixed(1)}</ele>`;
}

function metadataTime(route: ExportableRoute) {
  if (!route.metadataTime || !Number.isFinite(route.metadataTime) || route.metadataTime <= 0) return "";
  return `\n    <time>${new Date(route.metadataTime).toISOString()}</time>`;
}

function waypointDescription(waypoint: ExportableWaypoint) {
  const details = [
    waypoint.description,
    waypoint.km ? `km: ${waypoint.km}` : "",
    waypoint.openTime ? `open: ${waypoint.openTime}` : "",
    waypoint.closeTime ? `close: ${waypoint.closeTime}` : "",
    typeof waypoint.visibleToAthletes === "boolean" ? `visibleToAthletes: ${waypoint.visibleToAthletes}` : ""
  ].filter(Boolean);

  return details.length ? `\n    <desc>${xmlEscape(details.join(" · "))}</desc>` : "";
}

function waypointXml(waypoint: ExportableWaypoint, fallbackIndex: number) {
  if (!Number.isFinite(waypoint.latitude) || !Number.isFinite(waypoint.longitude)) return "";

  const title = waypoint.title || waypoint.type || `PULS waypoint ${fallbackIndex + 1}`;
  const type = waypoint.type ? `\n    <type>${xmlEscape(waypoint.type)}</type>` : "";
  const symbol = waypoint.symbol ? `\n    <sym>${xmlEscape(waypoint.symbol)}</sym>` : "";
  return `  <wpt lat="${waypoint.latitude.toFixed(7)}" lon="${waypoint.longitude.toFixed(7)}">
    <name>${xmlEscape(title)}</name>${waypointDescription(waypoint)}${type}${symbol}
  </wpt>`;
}

function controlPointWaypoint(point: GeneratedRoutePoint): ExportableWaypoint {
  return {
    latitude: point.latitude,
    longitude: point.longitude,
    title: point.pointOfInterestName,
    description: point.pointOfInterestDescription,
    type: point.pointOfInterestType,
    symbol: point.pointOfInterestSymbol
  };
}

export function safeFileName(name: string, fallback = "route.gpx") {
  const baseName = name.trim() || fallback.replace(/\.gpx$/i, "");
  const withoutExtension = baseName.replace(/\.gpx$/i, "");
  const cleanedBase = withoutExtension
    .replace(/\s+/g, "_")
    .replace(/[<>:"/\\|?*\x00-\x1F]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^[_\-.]+|[_\-.]+$/g, "")
    .slice(0, 116);

  return cleanedBase ? `${cleanedBase}.gpx` : fallback;
}

export function buildGpxFromRoute(route: ExportableRoute) {
  const title = route.title || "PULS route";
  const waypoints = [
    ...(route.controlPoints ?? []).map(controlPointWaypoint),
    ...(route.waypoints ?? [])
  ];
  const waypointPoints = waypoints
    .map(waypointXml)
    .filter(Boolean)
    .join("\n");
  const trackPoints = route.routePoints
    .filter((point) => Number.isFinite(point.latitude) && Number.isFinite(point.longitude))
    .map(
      (point) => `      <trkpt lat="${point.latitude.toFixed(7)}" lon="${point.longitude.toFixed(7)}">${pointElevation(point)}${pointTime(point)}
      </trkpt>`
    )
    .join("\n");

  return `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="PULS" xmlns="http://www.topografix.com/GPX/1/1">
  <metadata>
    <name>${xmlEscape(title)}</name>${metadataTime(route)}
  </metadata>
${waypointPoints ? `${waypointPoints}\n` : ""}  <trk>
    <name>${xmlEscape(title)}</name>
    <trkseg>
${trackPoints}
    </trkseg>
  </trk>
</gpx>
`;
}

export function downloadTextFile(filename: string, contents: string, type = "application/gpx+xml") {
  const blob = new Blob([contents], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function downloadGpxRoute(route: ExportableRoute, filename?: string) {
  const gpx = buildGpxFromRoute(route);
  downloadTextFile(filename ?? safeFileName(`puls-${route.title || "route"}`, "puls-route.gpx"), gpx);
}
